'use client';
// Browser talks only to same-origin `/api/*`. The Next.js route handler
// reverse-proxies those calls to `{brand}.fxcrm.live/client/*`.
import axios from 'axios';

export function getBaseURL(): string {
  return '/api/';
}

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  // Safari rejects multipart uploads when Content-Type is forced without a
  // boundary. Let the browser set it for FormData bodies.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
    const headers = config.headers as { delete?: (name: string) => void } & Record<string, unknown>;
    if (typeof headers.delete === 'function') {
      headers.delete('Content-Type');
      headers.delete('content-type');
    } else {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
  }

  try {
    if (typeof localStorage !== 'undefined') {
      const accessToken = localStorage.getItem('access_token');
      const accountId = localStorage.getItem('account_id');
      if (accessToken) config.headers['x-api-key'] = accessToken;
      if (accountId) config.headers['x-account-id'] = accountId;
    }
  } catch {
    // silent fail
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject(error)
);

export default api;

/**
 * Fetches design config and WS URL from the design endpoint.
 * Stores results in localStorage and dispatches update events.
 * On auth pages, do NOT pass auth headers (they are not set yet).
 */
export async function fetchDesignAndWS(): Promise<{ wsUrl: string }> {
  try {
    // Public endpoint: do not send cached auth headers from localStorage.
    const publicApi = axios.create({
      baseURL: getBaseURL(),
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' },
    });
    const res = await publicApi.post('design', null);
    const data = res.data;

    if (typeof localStorage !== 'undefined') {
      if (data?.settings?.currency) localStorage.setItem('currency', data.settings.currency);
      if (data?.design) localStorage.setItem('design', JSON.stringify(data.design));
      if (data?.settings) localStorage.setItem('settings', JSON.stringify(data.settings));
      if (data?.content?.languages)
        localStorage.setItem('languages', JSON.stringify(data.content.languages));
      if (data?.design?.theme) localStorage.setItem('app-theme', data.design.theme);
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('languagesUpdated'));
      window.dispatchEvent(new Event('designUpdated'));
    }

    const wsUrl: string = data?.urls?.ws_url || 'wss://socket.fxcrm.live:2096/';
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('ws_url', wsUrl);
    }
    return { wsUrl };
  } catch {
    return { wsUrl: 'wss://socket.fxcrm.live:2096/' };
  }
}

/**
 * Reads the cached base currency from the /design endpoint response
 * (localStorage key 'currency'). Falls back to the provided fallback
 * (default 'USD') if the design config has not been fetched yet.
 */
export function getCurrency(fallback = 'USD'): string {
  if (typeof localStorage === 'undefined') return fallback;
  try {
    return localStorage.getItem('currency') || fallback;
  } catch {
    return fallback;
  }
}

export function getCurrencySymbol(): string {
  return getCurrency();
}

/**
 * Reads the cached branding title from the /design endpoint response
 * (localStorage key 'design'). Falls back to a generic, non-branded name
 * if the design config has not been fetched yet.
 */
export function getDesignTitle(fallback = 'Trading Platform'): string {
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem('design');
    if (raw) {
      const design = JSON.parse(raw);
      return design?.title || design?.company_name || fallback;
    }
  } catch {
    // ignore
  }
  return fallback;
}

/**
 * Slugifies the cached /design title into a domain-safe token
 * (e.g. "Client Trader" → "clienttrader"). Used to derive brand-specific
 * emails/URLs without hardcoding a company name.
 */
export function getDesignDomainSlug(fallback = 'trading-platform'): string {
  const title = getDesignTitle('');
  if (!title) return fallback;
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '');
  return slug || fallback;
}

/**
 * Derives a support email address from the cached /design title
 * (e.g. "Client" → "support@client.com"). Used where a hardcoded
 * brand-specific support email would otherwise be shown.
 */
export function getDesignSupportEmail(fallback = 'support@trading-platform.com'): string {
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem('design');
    if (raw) {
      const design = JSON.parse(raw);
      if (design?.support_email) return design.support_email;
    }
  } catch {
    // ignore
  }
  return `support@${getDesignDomainSlug()}.com`;
}

/**
 * Reads the cached /design object from localStorage.
 */
export function getCachedDesign(): Record<string, unknown> | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('design');
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

/**
 * Updates the document favicon to the provided design favicon URL.
 */
export function applyDesignFavicon(faviconUrl?: string): void {
  if (typeof document === 'undefined' || !faviconUrl) return;
  let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}
